/**
 * Module dependencies.
 */

import { AuthField, AuthShell, AuthSubmit } from 'app/components/pages/auth/auth-shell';
import { Logo } from 'app/components/pages/landing/brand';
import { MailCheck } from 'lucide-react';
import { appRoutes } from 'app/routes';
import { useForgotPassword } from 'app/hooks/requests/use-forgot-password';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';

/**
 * Export `ForgotPasswordPage` component.
 */

export function ForgotPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { isLoading, mutate: forgot } = useForgotPassword({ onSuccess: () => setSubmitted(true) });

  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!emailValid) {
      return;
    }

    forgot(email.trim());
  }

  if (submitted) {
    return (
      <AuthShell>
        <div className={'flex justify-center'}>
          <Logo size={40} />
        </div>

        <div
          className={'mx-auto mt-8 grid h-14 w-14 place-items-center rounded-2xl'}
          style={{ backgroundColor: 'rgba(34,165,68,0.12)' }}
        >
          <MailCheck className={'h-7 w-7'} style={{ color: '#1B752B' }} />
        </div>

        <h1 className={"mt-6 font-['Outfit'] text-2xl font-semibold tracking-[-0.03em]"}>{'Check your email'}</h1>

        <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>
          {'If an account exists for '}

          <span className={'font-medium text-neutral-800'}>{email.trim()}</span>

          {', we sent a link to reset your password.'}
        </p>

        <p className={'mt-8 text-sm text-neutral-500'}>
          {'Back to '}

          <button
            className={'font-medium text-[#1B752B] hover:underline'}
            onClick={() => navigate(appRoutes.login)}
            type={'button'}
          >
            {'Sign in'}
          </button>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className={'flex justify-center'}>
        <Logo size={40} />
      </div>

      <h1 className={"mt-8 font-['Outfit'] text-3xl font-semibold tracking-[-0.03em]"}>{'Reset your password'}</h1>

      <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>
        {'Enter your email and we will send you a link to choose a new password.'}
      </p>

      <form className={'mt-8 space-y-4'} onSubmit={onSubmit}>
        <AuthField autoComplete={'email'} label={'Email'} onChange={setEmail} type={'email'} value={email} />

        <AuthSubmit disabled={!emailValid || isLoading}>{isLoading ? 'Sending…' : 'Send reset link'}</AuthSubmit>
      </form>

      <p className={'mt-8 text-sm text-neutral-500'}>
        {'Back to '}

        <button
          className={'font-medium text-[#1B752B] hover:underline'}
          onClick={() => navigate(appRoutes.login)}
          type={'button'}
        >
          {'Sign in'}
        </button>
      </p>
    </AuthShell>
  );
}
